"use client";

import { GenericButton } from "@capex/components/general/button.component";
import { Spinner, Input } from "@material-tailwind/react";
import { useTransition, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { sendOTP, signinUser } from "@capex/services/apiQueries/user";
import { useRouter } from "next/navigation";
import OtpInput from "react-otp-input";
import Link from "next/link";
import validator from "validator";

export default function SigninPage() {
  const [stage, setStage] = useState<number>(1);
  const [isPending, startTransition] = useTransition();
  const [signinData, setSigninData] = useState<{ email: string }>({
    email: "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: signinData,
  });

  const onSubmit = handleSubmit(async (data) => {
    startTransition(async () => {
      setSigninData({
        email: data.email,
      });
      try {
        const res = await signinUser(data.email);
        const phoneNumber = res?.mobileNumber;
        if (!phoneNumber) {
          toast.error("An error occured, phoneNumber not found");
          return;
        }
        const otpRes = await sendOTP({
          email: data.email,
          phone: phoneNumber,
        });
        if (otpRes === "An error occurred") {
          toast.error("An error occurred, please try again");
          return;
        }
        setStage(2);
      } catch (error) {
        console.error(error);
        toast.error("An unexpected error occurred.");
      }
    });
  });

  return (
    <>
      {stage === 1 && (
        <div className="flex justify-center items-center w-full h-full">
          <div className="flex flex-col items-center gap-8 w-full">
            <p className="font-semibold text-3xl text-primary">Sign In</p>
            <form
              onSubmit={onSubmit}
              className="flex flex-col items-center gap-8 w-full"
            >
              <div className="flex flex-col items-center gap-[18px] w-full md:w-[450px]">
                <div className="flex flex-col gap-1 w-full">
                  <Input
                    variant="static"
                    color="black"
                    placeholder="Email Address"
                    {...register("email", {
                      required: true,
                      validate: (value) => validator.isEmail(value),
                    })}
                    {...({} as any)}
                  />
                  {errors?.email && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors.email?.type === "required" && "Email is required"}
                      {errors.email?.type === "validate" &&
                        "Email must be valid"}
                    </p>
                  )}
                </div>
                <GenericButton
                  type="submit"
                  className="bg-secondary mt-8 flex justify-center items-center gap-x-3 !w-full"
                >
                  {isPending ? (
                    <div className="flex justify-center items-center">
                      <Spinner className="h-6 w-6" {...({} as any)} />
                    </div>
                  ) : (
                    "Get OTP"
                  )}
                </GenericButton>
                <p className="text-text-mid font-normal mt-1">
                  Don&apos;t have an account yet?
                  <Link href={"/auth/signup"}>
                    <span className="text-text-blue_accent font-medium text-[17px] ml-2">
                      Sign Up
                    </span>
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      )}
      {stage === 2 && <SigninOTPverificationComponent formData={signinData} />}
    </>
  );
}

const SigninOTPverificationComponent = ({
  formData,
}: {
  formData: { email: string };
}) => {
  const [otp, setOtp] = useState<string>("");
  const [isPending, startTransition] = useTransition();
  const [loading, setLoading] = useState<boolean>(false);
  const router = useRouter();

  const handleOTPVerification = async () => {
    setLoading(true);
    try {
      const res = await signinUser(formData.email);
      if (res?.email !== formData.email) {
        toast.error(
          res?.email === "julzore@gmail.com"
            ? "User does not exist"
            : "An error occurred"
        );
        setLoading(false);
        return;
      }

      const response = await fetch("/api/auth/signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: res.email,
          firstName: res.firstName,
          lastName: res.lastName,
          phoneNumber: res.mobileNumber,
          otp,
        }),
      });

      if (response.ok) {
        startTransition(() => {
          // The signin route revalidates the root layout server-side.
          // router.refresh() forces the client Router cache to drop its
          // stale RSC payload (otherwise the sidebar can stay in the
          // logged-out state after navigating to /console).
          router.replace("/console");
          router.refresh();
        });
      } else {
        const bodyText = await response.text();
        console.error(
          "Signin failed:",
          response.status,
          bodyText.slice(0, 500)
        );
        setLoading(false);
        toast.error("An error occurred");
      }
    } catch (error) {
      console.error(error);
      toast.error("An unexpected error occurred.");
      setLoading(false);
    }
  };

  return (
    <div className="h-full grid grid-cols-1 place-content-center text-center">
      <div className="mb-9">
        <p className="font-semibold text-3xl text-primary">OTP Verification</p>
      </div>

      <OtpInput
        value={otp}
        onChange={setOtp}
        numInputs={6}
        renderInput={(props) => <input {...props} />}
        containerStyle="flex justify-center items-center gap-2 md:gap-4 w-full mb-8"
        inputStyle="!w-[45px] md:!w-[72px] h-[40px] md:h-[60px] text-lg md:text-2xl font-medium text-text-dark border-[1px] border-[#D9D9D9] rounded-[8px] focus:outline-none focus:border-primary"
      />

      <div className="w-full md:w-[450px] mx-auto md:mt-3 mb-5">
        <GenericButton
          className="bg-secondary mx-auto !w-full cursor-pointer disabled:bg-disabled"
          disabled={otp.length !== 6}
          onClick={handleOTPVerification}
        >
          {isPending || loading ? (
            <div className="flex justify-center items-center gap-2">
              <Spinner className="w-6 h-6" {...({} as any)} />
              <p>Verifying</p>
            </div>
          ) : (
            "Verify"
          )}
        </GenericButton>
      </div>

      <p className="text-text-mid">
        Didn&apos;t receive OTP code?
        <span className="font-medium text-primary cursor-pointer ml-2 text-[17px]">
          Resend Now
        </span>
      </p>
    </div>
  );
};
